import { useMemo } from "react";
import * as React from "react";
import { View, StyleSheet, FlatList } from "react-native";

import {
  Typography,
  Box,
  SelectOption,
  ActivityIndicator,
  FlatListItemSeparator,
  useTheme,
  PanelHeaderTitle,
} from "@smartrent/ui";

import { useGroupThermostatSettingsQuery } from "@/api";
import { ListItemLabelValueCompact } from "@/common/ListItemLabelValueCompact";

import {
  formatEnabledWithValue,
  formatSetpoint,
  formatHumidistatRuntime,
} from "@/utils/thermostat-settings";

import { useGroup } from "../GroupContext";

interface ThermostatSettingsSection {
  title: string;
  items: SelectOption[];
}

export const GroupThermostatSettings: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { group } = useGroup();
  const { colors } = useTheme();

  const {
    data: groupThermostatSettings,
    isLoading: groupThermostatSettingsLoading,
  } = useGroupThermostatSettingsQuery({
    groupId: group?.id,
  });

  const sections: ThermostatSettingsSection[] = useMemo(() => {
    const config = groupThermostatSettings?.data;

    return [
      {
        title: "Thermostat Alert Settings",
        items: [
          {
            label: "Low Temperature Alert",
            value: formatEnabledWithValue(
              config?.low_temp_alerts_enabled,
              config?.safe_temp_range_floor
            ),
          },
          {
            label: "High Temperature Alert",
            value: formatEnabledWithValue(
              config?.high_temp_alerts_enabled,
              config?.safe_temp_range_ceiling
            ),
          },
          {
            label: "Humidity Alert",
            value: formatEnabledWithValue(
              config?.humidity_alerts_enabled,
              config?.humidity_range_ceiling,
              "%"
            ),
          },
          {
            label: "Battery Threshold",
            value: formatSetpoint(config?.battery_threshold, "%"),
          },
        ],
      },
      {
        title: "Safety Control Settings",
        items: [
          {
            label: "Safety Heating Control",
            value: formatEnabledWithValue(
              config?.safety_set_point_heat_enabled,
              config?.safety_set_point_heat
            ),
          },
          {
            label: "Safety Cooling Control",
            value: formatEnabledWithValue(
              config?.safety_set_point_cool_enabled,
              config?.safety_set_point_cool
            ),
          },
          {
            label: "Safety Humidity Control",
            value: formatEnabledWithValue(
              config?.humidistat_control_enabled,
              config?.humidistat_control_range_ceiling,
              "%"
            ),
          },
          {
            label: "Maximum Cooling Cycle Time",
            value: formatHumidistatRuntime(
              config?.humidistat_control_maximum_runtime,
              config?.humidistat_control_enabled
            ),
          },
        ],
      },
      {
        title: "Vacant Unit Thermostat Settings",
        items: [
          {
            label: "Vacant Heating Setpoint",
            value: formatSetpoint(config?.vacant_heating_setpoint),
          },
          {
            label: "Vacant Cooling Setpoint",
            value: formatSetpoint(config?.vacant_cooling_setpoint),
          },
          {
            label: "Vacant After-hours Heating Setpoint",
            value: formatSetpoint(config?.vacant_after_hours_heating_setpoint),
          },
          {
            label: "Vacant After-hours Cooling Setpoint",
            value: formatSetpoint(config?.vacant_after_hours_cooling_setpoint),
          },
        ],
      },
    ];
  }, [groupThermostatSettings]);

  return (
    <Box style={styles.cardStyle}>
      <View
        style={[
          styles.headingWrapper,
          { backgroundColor: colors.pageBackground },
        ]}
      >
        <PanelHeaderTitle title="Thermostat Settings" />
      </View>
      <View style={styles.cardWrapper}>
        {groupThermostatSettingsLoading ? (
          <View style={styles.centerAlign}>
            <ActivityIndicator />
          </View>
        ) : groupThermostatSettings?.data ? (
          <View style={styles.sections}>
            {sections.map((section) => (
              <View key={section.title}>
                <View
                  style={[
                    styles.sectionTitle,
                    { borderBottomColor: colors.gray100 },
                  ]}
                >
                  <Typography type="title5">
                    {section.title.toUpperCase()}
                  </Typography>
                </View>
                <FlatList<SelectOption>
                  data={section.items}
                  keyExtractor={({ label }) => label}
                  renderItem={({ item: { label, value } }) => (
                    <ListItemLabelValueCompact
                      label={label}
                      value={value}
                      justifySpaceBetween
                    />
                  )}
                  ItemSeparatorComponent={FlatListItemSeparator}
                />
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.centerAlign}>
            <Typography type="title6" color="textDisabled">
              No thermostat settings found.
            </Typography>
          </View>
        )}
      </View>
    </Box>
  );
};

const styles = StyleSheet.create({
  headingWrapper: {
    flexDirection: "row",
    height: 72,
    alignItems: "center",
    padding: 24,
  },
  cardWrapper: {
    flex: 1,
    marginBottom: 8,
    borderTopColor: "transparent",
  },
  cardStyle: { padding: 0 },
  centerAlign: { alignItems: "center" },
  sections: {
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  sectionTitle: {
    borderBottomWidth: 1,
    paddingBottom: 8,
    marginBottom: 8,
    marginTop: 16,
  },
});
